import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Calendar, Building2, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";

export default function ProjectDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setItem(null);
    api.get(`/projects/${slug}`).then((r) => setItem(r.data)).catch(() => nav("/projects"));
    api.get("/projects").then((r) => setRelated(r.data.filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug, nav]);

  if (!item) return <div className="mia-container py-32">Loading…</div>;

  return (
    <div>
      <section className="relative min-h-[65vh] flex items-end overflow-hidden bg-[color:var(--mia-navy)]">
        <img src={item.hero_image || item.cover_image} alt={item.title} className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative mia-container py-14 lg:py-16">
          <Link to="/projects" className="text-white/80 hover:text-[color:var(--mia-gold)] text-sm inline-flex items-center gap-2 mb-4">
            <ArrowLeft className="w-4 h-4" /> All Projects
          </Link>
          <div className="eyebrow text-[color:var(--mia-gold)]">{item.category} · {item.year}</div>
          <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.02] mt-4 max-w-4xl">
            {item.title}
          </h1>
          <div className="mt-8 flex flex-wrap gap-6 text-white/85 text-sm">
            <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-[color:var(--mia-gold)]" /> {item.client}</div>
            <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[color:var(--mia-gold)]" /> {item.location}</div>
            <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[color:var(--mia-gold)]" /> {item.year}</div>
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-16 bg-white">
        <div className="mia-container grid lg:grid-cols-[2fr,1fr] gap-14">
          <div>
            <div className="eyebrow">Project Overview</div>
            <div className="divider-gold" />
            <p className="text-lg leading-relaxed text-[color:var(--mia-text)]">{item.overview}</p>

            {["scope", "challenges", "solutions"].map((k) => (
              <div key={k} className="mt-12">
                <h3 className="font-display text-2xl font-bold text-[color:var(--mia-navy)] capitalize">{k}</h3>
                <div className="w-10 h-[3px] bg-[color:var(--mia-gold)] mt-2 mb-4" />
                <p className="text-[color:var(--mia-muted)] leading-relaxed whitespace-pre-line">{item[k]}</p>
              </div>
            ))}

            {item.gallery?.length > 0 && (
              <div className="mt-14">
                <div className="eyebrow">Gallery</div>
                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  {item.gallery.map((g, i) => (
                    <img key={i} src={g} alt={`gallery ${i}`} className="w-full aspect-[4/3] object-cover rounded-2xl" />
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="lg:sticky top-32 h-fit space-y-6">
            <div className="p-8 rounded-2xl bg-[color:var(--mia-navy)] text-white">
              <div className="eyebrow text-[color:var(--mia-gold)]">Interested in a similar project?</div>
              <h3 className="font-display text-2xl font-black mt-3 leading-tight">Let's talk delivery, timeline and budget.</h3>
              <Link to="/contact" className="mia-btn mia-btn-primary w-full justify-center mt-6" data-testid="project-cta-contact">Request a Proposal</Link>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-14 lg:py-16 bg-[color:var(--mia-grey)]">
          <div className="mia-container">
            <h3 className="font-display text-3xl sm:text-4xl font-black text-[color:var(--mia-navy)] tracking-tight mb-10">More Projects</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link key={r.id} to={`/projects/${r.slug}`} className="group rounded-2xl overflow-hidden bg-white border border-slate-100 card-lift">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={r.cover_image} alt={r.title} className="w-full h-full object-cover image-zoom" />
                  </div>
                  <div className="p-5">
                    <div className="text-[10px] tracking-[0.24em] uppercase text-[color:var(--mia-gold)] font-bold">{r.category}</div>
                    <div className="font-display font-bold text-lg text-[color:var(--mia-navy)] mt-2">{r.title}</div>
                    <div className="mt-3 text-sm text-[color:var(--mia-blue)] font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                      View project <ArrowUpRight className="w-3 h-3" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
